import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Modal,
  Fade,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { showAlert, confirmAsync } from '../../../../utils/functions/globalDialog';
import { fmtMoney } from './ProjectSandboxPendingItemsModal';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '96vw',
  maxWidth: 760,
  maxHeight: '92vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 3,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const REFUND_PENDING_COLOR = '#0288d1';
export const REFUND_RECEIVED_COLOR = '#2e7d32';

const genLocalId = () => `sbx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Sandbox fork of ../../../../components/Modal/RefundItemsModal.js — เหตุผล/pattern เดียวกับ
// ProjectSandboxPendingItemsModal.js เป๊ะ (ของจริง dispatch(actions.budgetAddRefundItem/...) +
// api.post อัปโหลดหลักฐานตรงๆ ทุกจุด แทนที่ด้วยการแก้ refund_items array ในเครื่องล้วนๆ)
export default function ProjectSandboxRefundItemsModal({ open, budget, onClose, onLocalUpdate }) {
  const [localBudget, setLocalBudget] = useState(budget);
  const [refundTab, setRefundTab] = useState(0); // 0 = รอคืน, 1 = คืนแล้ว
  const [refundForm, setRefundForm] = useState({ name: '', amount: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: '' });
  const [receiveItem, setReceiveItem] = useState(null);

  useEffect(() => {
    if (open) {
      setLocalBudget(budget);
      setRefundTab(0);
    }
  }, [open, budget]);

  const refundItemsAll = localBudget?.refund_items || [];
  const refundItemsPending = _.filter(refundItemsAll, (i) => !i.refunded);
  const refundItemsReceived = _.filter(refundItemsAll, (i) => i.refunded);
  const refundItemsShown = refundTab === 0 ? refundItemsPending : refundItemsReceived;

  const applyUpdate = (updated) => {
    setLocalBudget(updated);
    onLocalUpdate?.(updated);
  };

  const openAddDialog = () => {
    setRefundForm({ name: '', amount: '' });
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setRefundForm({ name: '', amount: '' });
  };

  const handleAddRefundItem = () => {
    const name = refundForm.name.trim();
    const amount = parseFloat(refundForm.amount);
    if (!name || isNaN(amount) || amount <= 0) {
      showAlert('กรุณากรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    const newItem = { _id: genLocalId(), name, amount, refunded: false, createdAt: new Date().toISOString() };
    applyUpdate({ ...localBudget, refund_items: [...refundItemsAll, newItem] });
    closeAddDialog();
    setRefundTab(0);
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setEditForm({ name: item.name, amount: String(item.amount) });
  };

  const closeEditDialog = () => {
    setEditItem(null);
    setEditForm({ name: '', amount: '' });
  };

  const handleUpdateRefundItem = () => {
    const name = editForm.name.trim();
    const amount = parseFloat(editForm.amount);
    if (!name || isNaN(amount) || amount <= 0) {
      showAlert('กรุณากรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    const nextItems = refundItemsAll.map((i) => (i._id === editItem._id ? { ...i, name, amount } : i));
    applyUpdate({ ...localBudget, refund_items: nextItems });
    closeEditDialog();
  };

  const handleRemoveRefundItem = async (itemId) => {
    if (!(await confirmAsync('ลบรายการนี้?'))) return;
    const nextItems = refundItemsAll.filter((i) => i._id !== itemId);
    applyUpdate({ ...localBudget, refund_items: nextItems });
  };

  const openReceiveDialog = (item) => setReceiveItem(item);
  const closeReceiveDialog = () => setReceiveItem(null);

  const handleConfirmReceive = () => {
    const nextItems = refundItemsAll.map((i) => (
      i._id === receiveItem._id ? { ...i, refunded: true, refundedAt: new Date().toISOString() } : i
    ));
    applyUpdate({ ...localBudget, refund_items: nextItems });
    closeReceiveDialog();
  };

  return (
    <>
      <Modal open={!!open} onClose={onClose}>
        <Fade in={!!open}>
          <Box sx={modalStyle}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    รายการเงินที่รอรับคืน (ทดสอบ)
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.6,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1.5,
                      bgcolor: alpha(REFUND_PENDING_COLOR, 0.1),
                      maxWidth: '100%',
                    }}
                  >
                    <Typography noWrap sx={{ fontWeight: 700, color: REFUND_PENDING_COLOR }}>
                      {localBudget?.prefix}{localBudget?.budget_number}
                    </Typography>
                    <Typography noWrap sx={{ fontWeight: 700, color: REFUND_PENDING_COLOR }}>
                      {localBudget?.name}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="เพิ่มรายการรอรับคืน">
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ bgcolor: REFUND_PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0270ad' } }}
                    >
                      เพิ่มรายการ
                    </Button>
                  </Tooltip>
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.2, mt: 2.5 }}>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(REFUND_PENDING_COLOR, 0.08), border: '1px solid', borderColor: alpha(REFUND_PENDING_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: REFUND_PENDING_COLOR, fontWeight: 700, display: 'block' }}>
                    รอคืน ({refundItemsPending.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: REFUND_PENDING_COLOR }}>
                    ฿{fmtMoney(_.sumBy(refundItemsPending, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(REFUND_RECEIVED_COLOR, 0.08), border: '1px solid', borderColor: alpha(REFUND_RECEIVED_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: REFUND_RECEIVED_COLOR, fontWeight: 700, display: 'block' }}>
                    คืนแล้ว ({refundItemsReceived.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: REFUND_RECEIVED_COLOR }}>
                    ฿{fmtMoney(_.sumBy(refundItemsReceived, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    รวมทั้งหมด ({refundItemsAll.length})
                  </Typography>
                  <Typography fontWeight={700}>
                    ฿{fmtMoney(_.sumBy(refundItemsAll, 'amount'))}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Tabs
              value={refundTab}
              onChange={(_ev, v) => setRefundTab(v)}
              variant="fullWidth"
              sx={{ minHeight: 42, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, '& .MuiTab-root': { minHeight: 42, fontSize: '0.85rem', fontWeight: 600, py: 0.5 } }}
            >
              <Tab label={`รอคืน (${refundItemsPending.length})`} sx={{ color: refundTab === 0 ? REFUND_PENDING_COLOR : undefined }} />
              <Tab label={`คืนแล้ว (${refundItemsReceived.length})`} sx={{ color: refundTab === 1 ? REFUND_RECEIVED_COLOR : undefined }} />
            </Tabs>

            <Box sx={{ overflowY: 'auto', flex: 1, px: 2, py: 1.5, bgcolor: alpha('#000', 0.015) }}>
              {_.isEmpty(refundItemsShown) ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: refundTab === 0 ? 1.5 : 0 }}>
                    {refundTab === 0 ? 'ยังไม่มีรายการรอรับคืน' : 'ยังไม่มีรายการที่ได้รับคืนแล้ว'}
                  </Typography>
                  {refundTab === 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ color: REFUND_PENDING_COLOR, borderColor: REFUND_PENDING_COLOR }}
                    >
                      เพิ่มรายการรอรับคืน
                    </Button>
                  )}
                </Box>
              ) : (
                _.orderBy(refundItemsShown, ['createdAt'], ['desc']).map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      '&:hover': { borderColor: item.refunded ? REFUND_RECEIVED_COLOR : REFUND_PENDING_COLOR },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(item.refunded ? REFUND_RECEIVED_COLOR : REFUND_PENDING_COLOR, 0.12),
                        }}
                      >
                        {item.refunded
                          ? <CheckCircleIcon sx={{ fontSize: 18, color: REFUND_RECEIVED_COLOR }} />
                          : <HourglassEmptyIcon sx={{ fontSize: 18, color: REFUND_PENDING_COLOR }} />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.refunded
                            ? `ได้รับคืนเมื่อ ${item.refundedAt ? dayjs(item.refundedAt).format('DD/MM/YYYY') : '-'}`
                            : `เพิ่มเมื่อ ${item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : '-'}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ mr: 0.5, color: item.refunded ? REFUND_RECEIVED_COLOR : 'text.primary', whiteSpace: 'nowrap' }}>
                        ฿{fmtMoney(item.amount)}
                      </Typography>
                      {!item.refunded && (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          sx={{ bgcolor: REFUND_PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0270ad' } }}
                          startIcon={<PaidIcon fontSize="small" />}
                          onClick={() => openReceiveDialog(item)}
                        >
                          ได้รับคืนแล้ว
                        </Button>
                      )}
                      {!item.refunded && (
                        <Tooltip title="แก้ไข">
                          <IconButton size="small" onClick={() => openEditDialog(item)} sx={{ color: REFUND_PENDING_COLOR }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="ลบรายการ">
                        <IconButton size="small" onClick={() => handleRemoveRefundItem(item._id)} sx={{ color: 'text.disabled' }}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircleOutlineIcon sx={{ color: REFUND_PENDING_COLOR }} />
          เพิ่มรายการรอรับคืน
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              label="ชื่อรายการ"
              value={refundForm.name}
              onChange={(ev) => setRefundForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
            />
            <TextField
              type="number"
              label="จำนวนเงิน"
              value={refundForm.amount}
              onChange={(ev) => setRefundForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAddDialog}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: REFUND_PENDING_COLOR, '&:hover': { bgcolor: '#0270ad' } }}
            onClick={handleAddRefundItem}
          >
            เพิ่มรายการ
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editItem} onClose={closeEditDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: REFUND_PENDING_COLOR }} />
          แก้ไขรายการรอรับคืน
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              label="ชื่อรายการ"
              value={editForm.name}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
            />
            <TextField
              type="number"
              label="จำนวนเงิน"
              value={editForm.amount}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: REFUND_PENDING_COLOR, '&:hover': { bgcolor: '#0270ad' } }}
            onClick={handleUpdateRefundItem}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      {/* ยืนยันได้รับเงินคืน — ตัดช่องแนบหลักฐานออก (เหตุผลเดียวกับ pay dialog ใน
          ProjectSandboxPendingItemsModal.js: ไม่มีปลายทางอัปโหลดจริงให้จำลอง) */}
      <Dialog open={!!receiveItem} onClose={closeReceiveDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaidIcon sx={{ color: REFUND_RECEIVED_COLOR }} />
          ยืนยันได้รับเงินคืน (ทดสอบ)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderRadius: 2, p: 1.5, bgcolor: alpha(REFUND_PENDING_COLOR, 0.08), border: '1px solid', borderColor: alpha(REFUND_PENDING_COLOR, 0.3) }}>
            <Typography variant="body2" color="text.secondary">{receiveItem?.name}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: REFUND_PENDING_COLOR }}>
              ฿{fmtMoney(receiveItem?.amount)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeReceiveDialog}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: REFUND_RECEIVED_COLOR, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={handleConfirmReceive}
          >
            ยืนยันได้รับเงินคืน
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
